import React from 'react';
import cx from 'classnames';

import Button from 'app/components/ui/Button';
import BookmarkSvg from 'app/components/common/Bookmark';
import { TABLET_WIDTH, QUIPUSWAP, QUIPUSWAP_LIQUIDITY } from 'app/utils/defaults';
import TokenImage from 'app/components/ui/TokenImage';
import { TokenDetailsType } from 'app/types/head-section';
import { useWindowSize } from 'app/utils/windowResize';
import DynamicsWithTooltip from 'app/components/ui/DynamicsWithTooltip';
import TokenAddressInfo from 'app/components/ui/TokenAddressInfo';
import { prettyPrice, switchCurrency } from 'app/utils/helpers';
import { ReactComponent as Tezos } from 'app/components/svg/Tezos.svg';
import { CurrencyEnum } from 'app/components/ui/CurrencySwitcher/CurrencySwitcher';

import s from './TokenHeadSection.module.sass';

type TokenHeadSectionProps = {
  token: TokenDetailsType
  xtzUsdQuote: number
  loading?: boolean
  id: string
  currency?: any
};

const TokenHeadSection: React.FC<TokenHeadSectionProps> = ({
  token,
  xtzUsdQuote,
  loading,
  id,
  currency,
}) => {
  const { width } = useWindowSize();

  const isTablet = width <= TABLET_WIDTH;

  const tokenId: string = `${token.standard === 'FA2' ? `_${token.tokenId}` : ''}`;

  return (
    <section className={cx(s.root)}>
      <div className={cx(s.header)}>
        <div className={cx(s.tokenInfoWrapper)}>
          <TokenImage
            firstTokenImage={token.icon}
            className={cx(s.token)}
            sizeT="large"
            tokensNames={{ firstTokenName: token.symbol }}
          />

          {loading ? '...' : (
            <h1 className={cx(s.title)}>
              {`${token.name ?? (token.symbol ? token.symbol : 'Unnamed')} (${token.symbol ?? 'Unnamed'})`}
            </h1>
          )}

          <div className={cx(s.mobileButtonsWrapper)}>
            <BookmarkSvg id={id} field="tokens" className={cx(s.bookmark)} />
          </div>
        </div>

        <div className={cx(s.tokenMobilePrice)}>
          <span className={cx(s.priceInUsd)}>
            {currency === CurrencyEnum.USD || token.symbol === 'XTZ'
              ? '$' : (
                <Tezos className={cx(s.tezos)} />
              )}
            {' '}
            {token.symbol === 'XTZ' ? prettyPrice(token.price) : switchCurrency({ value: token.price, xtzUsdQuote, currency })}
          </span>
          <DynamicsWithTooltip className={cx(s.procent)} change={token.change} />
        </div>

        <div className={cx(s.buttonsWrapper)}>
          <Button
            sizeT="medium"
            href={`${QUIPUSWAP_LIQUIDITY}${token.id}${tokenId}`}
            className={cx(s.button)}
          >
            + Add liquidity
          </Button>

          <Button
            sizeT={isTablet ? 'medium' : 'small'}
            href={`${QUIPUSWAP}?from=tez&to=${token.id}${tokenId}`}
            className={cx(s.button, s.desktop, s.tokenMobile)}
          >
            Trade
          </Button>

          <BookmarkSvg id={id} field="tokens" className={cx(s.desktop)} />
        </div>
      </div>

      <div className={cx(s.wrapper)}>
        <div className={cx(s.priceWrapper)}>
          {loading ? (
            <span className={cx(s.priceLoading)}>
              ...
            </span>
          )
            : (
              <>
                <span className={cx(s.priceInUsd)}>
                  {currency === CurrencyEnum.USD || token.symbol === 'XTZ'
                    ? '$' : (
                      <Tezos className={cx(s.tezos)} />
                    )}
                  {' '}
                  {token.symbol === 'XTZ' ? prettyPrice(token.price) : switchCurrency({ value: token.price, xtzUsdQuote, currency })}
                </span>
                <DynamicsWithTooltip className={cx(s.procent)} change={token.change} />
              </>
            )}
        </div>

        <TokenAddressInfo
          address={token.address}
          tokenId={token.symbol !== 'XTZ' ? token.tokenId : null}
          className={cx(s.tokenInfo)}
          loading={loading}
        />
      </div>
    </section>
  );
};

export default TokenHeadSection;
